import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarCheck } from "lucide-react";

export default function Weeks({ spec, tone }) {
  const [v, setV] = useState(spec.items[0].at);
  const items = spec.items;
  const cur = useMemo(() => [...items].reverse().find((x) => x.at <= v) || items[0], [v, items]);
  const span = spec.to - spec.from;

  return (
    <div data-testid="repro-weeks">
      <div className="relative pt-8 pb-2">
        <div className="h-1.5 rounded-full relative" style={{ background: "rgba(58,26,42,0.09)" }}>
          <motion.span className="absolute left-0 top-0 h-full rounded-full" animate={{ width: `${((v - spec.from) / span) * 100}%` }} transition={{ duration: 0.3 }} style={{ background: tone }} />
          {items.map((x) => {
            const p = ((x.at - spec.from) / span) * 100;
            const on = cur.at === x.at;
            return (
              <button key={x.at} onClick={() => setV(x.at)} className="absolute -top-[7px]" style={{ left: `calc(${p}% - 9px)` }} data-testid={`weeks-marker-${x.at}`} aria-label={`${spec.unit} ${x.at}`}>
                <span className="block rounded-full transition-all" style={{ width: on ? 18 : 14, height: on ? 18 : 14, marginTop: on ? -2 : 0, background: on ? tone : x.appt ? "#fff" : "rgba(58,26,42,0.22)", border: x.appt ? `2px solid ${tone}` : "none", boxShadow: on ? `0 8px 20px -6px ${tone}` : "none" }} />
              </button>
            );
          })}
        </div>
        <input type="range" min={spec.from} max={spec.to} value={v} onChange={(e) => setV(Number(e.target.value))}
          className="w-full mt-6" style={{ accentColor: tone }} aria-label={spec.unit} data-testid="weeks-slider" />
        <div className="flex justify-between mt-1 text-[11px]" style={{ color: "rgba(58,26,42,0.35)" }}>
          <span>{spec.unit} {spec.from}</span><span>{spec.unit} {spec.to}</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={cur.at} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.35 }}
          className="mt-6 rounded-[1.8rem] p-7 sm:p-9 relative overflow-hidden" style={{ background: "rgba(255,255,255,0.88)", border: "1px solid rgba(58,26,42,0.07)", boxShadow: "0 40px 80px -60px rgba(58,26,42,0.7)" }}>
          <span className="absolute -right-16 -top-16 w-52 h-52 rounded-full halo" style={{ background: `radial-gradient(circle, ${tone}33, transparent 70%)` }} />
          <p className="relative text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: tone }}>{spec.unit} {cur.at}</p>
          <h3 className="relative font-display text-[30px] sm:text-[38px] leading-tight mt-2" style={{ color: "#3A1A2A" }}>{cur.t}</h3>
          <p className="relative text-[15.5px] mt-3.5 leading-relaxed max-w-2xl" style={{ color: "rgba(58,26,42,0.68)" }}>{cur.body}</p>
          {cur.appt && (
            <div className="relative mt-5 rounded-2xl px-5 py-4 flex items-start gap-3.5" style={{ background: `${tone}16`, border: `1px solid ${tone}3A` }} data-testid="weeks-appt">
              <CalendarCheck size={16} className="mt-0.5 shrink-0" style={{ color: tone }} />
              <p className="text-[14px]" style={{ color: "#3A1A2A" }}>{cur.appt}</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
